import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProgressBarHeader } from 'components';
import { routeConstants } from 'routes';
import { selectInvite } from 'store/Invites';
import { selectUserInvites } from 'store/User/selectors';
import { EmployeeType, IInvite, SiteRole } from 'types';
import { AccountSetupContent } from './components';

interface IAccountSetupProps {
  children: ReactNode;
  currentStep: number;
  hideProgress?: boolean;
}

export const AdminOnboardingContext = createContext(null);

const isTipIneligibleAdmin = (invite: IInvite) =>
  invite.employee.sites.length &&
  invite.employee.sites?.every(
    (site) =>
      site.role === SiteRole.ADMIN && ((invite.employee.type === EmployeeType.ADMIN && !site.settings?.rolePolicy?.organizationAdmin?.payable) || (invite.employee.type === EmployeeType.USER && !site.settings?.rolePolicy?.siteAdmin.payable))
  );

const AccountSetupLayout = ({ children, currentStep, hideProgress = false }: IAccountSetupProps) => {
  const invite = useSelector(selectInvite);
  const userInvites = useSelector(selectUserInvites);
  const [isTipIneligible, setIsTipIneligible] = useState<boolean>(false);

  useEffect(() => {
    if (invite) {
      setIsTipIneligible(isTipIneligibleAdmin(invite));
    } else if (userInvites?.length) {
      const allInvitesTipIneligible = userInvites.every((invite) => isTipIneligibleAdmin(invite));
      setIsTipIneligible(allInvitesTipIneligible);
    }
  }, [invite, userInvites]);

  const backButtonURL = (() => {
    switch (currentStep) {
      case 2:
        return routeConstants.private.accountSetup.notifications;
      case 3:
        return routeConstants.private.accountSetup.avatar;
      default:
        return undefined;
    }
  })();

  return (
    <>
      <AccountSetupContent data-cy="account-setup-layout">
        {!hideProgress && <ProgressBarHeader currentStep={currentStep} hideBack={currentStep === 1} label="Set Up Your Account" to={backButtonURL} totalSteps={isTipIneligible ? 2 : 3} />}

        <AdminOnboardingContext.Provider value={isTipIneligible}>{children}</AdminOnboardingContext.Provider>
      </AccountSetupContent>
    </>
  );
};

export default AccountSetupLayout;
